import { useCallback, useEffect, useRef } from 'react';

import { useAnalyticsEvents } from '@atlaskit/analytics-next/useAnalyticsEvents';

import type { EditorRemixLifecyclePhase } from '@atlassian/malleable-ui-components/types';
import { deriveMachineEditorRemixPhase } from '@atlassian/malleable-ui-components/editor-remix/lifecycle';
import {
	getRemixMachine,
	subscribeToMachine,
} from '@atlassian/malleable-ui-components/state/machine';

import { useGetPageMode } from '@confluence/page-utils/entry-points/useGetPageMode.ts';

import {
	createObjectSidebarDivergenceSignature,
	dispatchObjectSidebarRemixParityEvent,
	dispatchObjectSidebarRemixParityMechanicEvent,
	type ObjectSidebarRemixParityDimensions,
} from './dispatchObjectSidebarRemixParityEvent.ts';
import {
	useObjectSidebarRemixLifecycleDiagnostics,
	type ObjectSidebarRemixLifecycleOperation,
} from './useObjectSidebarRemixLifecycleDiagnostics.ts';
import {
	compareObjectSidebarRemixPhases,
	objectSidebarComparisonPointForLegacyPhase,
	type ObjectSidebarRemixPhase,
} from './objectSidebarRemixParity.ts';
import { durationBucketForMs } from './remixParityObservability.ts';

const SETTLE_MS = 250;

type Flow = {
	remixId: string;
	observationToken?: symbol;
	entryPath: ObjectSidebarRemixParityDimensions['entryPath'];
	legacyPhase: ObjectSidebarRemixPhase;
	hasLegacyObservation: boolean;
	reportedDivergences: Set<string>;
	reportedMechanics: Set<string>;
	pendingMechanic?: {
		legacyPhase: ObjectSidebarRemixPhase;
		machinePhase: ObjectSidebarRemixPhase;
		comparisonPoint: ReturnType<typeof objectSidebarComparisonPointForLegacyPhase>;
		signature: string;
		startedAt: number;
		emitted: boolean;
	};
	settleTimeout?: ReturnType<typeof setTimeout>;
	unsubscribe: () => void;
};

const isTerminalSidebarPhase = (phase: ObjectSidebarRemixPhase): boolean =>
	phase === 'handed-off' ||
	phase === 'inserted' ||
	phase === 'insertion-failed' ||
	phase === 'error' ||
	phase === 'aborted';

const toSidebarPhase = (
	phase: EditorRemixLifecyclePhase | ObjectSidebarRemixPhase,
): ObjectSidebarRemixPhase => {
	if (phase === 'inserting') {
		return 'previewing';
	}
	return phase;
};

export type ObjectSidebarRemixParityDiagnostics = Readonly<{
	observeFlow: (
		remixId: string,
		entryPath?: ObjectSidebarRemixParityDimensions['entryPath'],
	) => void;
	bindObservationToken: (remixId: string, token: symbol) => void;
	recordLegacyPhase: (
		token: symbol | undefined,
		legacyPhase: EditorRemixLifecyclePhase | ObjectSidebarRemixPhase,
		entryPath?: ObjectSidebarRemixParityDimensions['entryPath'],
	) => void;
	releaseFlow: (remixId: string, capturedMachinePhase?: ObjectSidebarRemixPhase) => void;
}>;

/** Read-only per-flow comparison. It never sends machine events or changes lifecycle ownership. */
export const useObjectSidebarRemixParityDiagnostics = (): ObjectSidebarRemixParityDiagnostics => {
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const reportLifecycleDiagnostic = useObjectSidebarRemixLifecycleDiagnostics();
	const pageMode = useGetPageMode();
	const flowsRef = useRef(new Map<string, Flow>());
	const activeFlowRef = useRef<string | undefined>(undefined);
	const reportObserverFailure = useCallback(
		(operation: ObjectSidebarRemixLifecycleOperation): void => {
			try {
				reportLifecycleDiagnostic('failed', { operation });
			} catch {
				// Observer diagnostics are best-effort and must never escape into Remix.
			}
		},
		[reportLifecycleDiagnostic],
	);
	const reportObserverFailureRef = useRef(reportObserverFailure);
	useEffect(() => {
		reportObserverFailureRef.current = reportObserverFailure;
	}, [reportObserverFailure]);

	const dimensions = useCallback(
		(
			entryPath: ObjectSidebarRemixParityDimensions['entryPath'],
		): ObjectSidebarRemixParityDimensions => ({
			entryPath,
			pageMode,
		}),
		[pageMode],
	);

	const reportCritical = useCallback(
		(
			flow: Flow,
			machinePhase: ObjectSidebarRemixPhase,
			reason: 'legacy-not-tracking' | 'terminal-outcome-mismatch' | 'released-unresolved',
		) => {
			const divergence = {
				legacyPhase: flow.legacyPhase,
				machinePhase,
				comparisonPoint: objectSidebarComparisonPointForLegacyPhase(flow.legacyPhase),
				divergenceReason: reason,
			};
			const signature = createObjectSidebarDivergenceSignature(divergence);
			if (flow.reportedDivergences.has(signature)) {
				return;
			}
			flow.reportedDivergences.add(signature);
			dispatchObjectSidebarRemixParityEvent(
				createAnalyticsEvent,
				divergence,
				dimensions(flow.entryPath),
			);
		},
		[createAnalyticsEvent, dimensions],
	);

	const evaluate = useCallback(
		(flow: Flow): void => {
			if (!flow.hasLegacyObservation) {
				return;
			}
			const machinePhase = toSidebarPhase(
				deriveMachineEditorRemixPhase(getRemixMachine(flow.remixId)?.state()),
			);
			const comparison = compareObjectSidebarRemixPhases(flow.legacyPhase, machinePhase);
			if (comparison === 'match') {
				if (flow.settleTimeout) {
					clearTimeout(flow.settleTimeout);
					flow.settleTimeout = undefined;
				}
				if (flow.pendingMechanic?.emitted) {
					dispatchObjectSidebarRemixParityMechanicEvent(
						createAnalyticsEvent,
						{
							legacyPhase: flow.pendingMechanic.legacyPhase,
							machinePhase: flow.pendingMechanic.machinePhase,
							comparisonPoint: flow.pendingMechanic.comparisonPoint,
							action: 'resolved',
							outcome: 'converged',
							reason: 'phase-converged',
							durationBucket: durationBucketForMs(Date.now() - flow.pendingMechanic.startedAt),
						},
						dimensions(flow.entryPath),
					);
				}
				flow.pendingMechanic = undefined;
				return;
			}
			if (comparison !== 'pending') {
				if (flow.settleTimeout) {
					clearTimeout(flow.settleTimeout);
					flow.settleTimeout = undefined;
				}
				reportCritical(flow, machinePhase, comparison.divergenceReason);
				return;
			}
			const signature = `${flow.legacyPhase}|${machinePhase}|${objectSidebarComparisonPointForLegacyPhase(flow.legacyPhase)}`;
			if (flow.pendingMechanic?.signature === signature) {
				return;
			}
			if (flow.settleTimeout) {
				clearTimeout(flow.settleTimeout);
			}
			flow.pendingMechanic = {
				legacyPhase: flow.legacyPhase,
				machinePhase,
				comparisonPoint: objectSidebarComparisonPointForLegacyPhase(flow.legacyPhase),
				signature,
				startedAt: Date.now(),
				emitted: false,
			};
			flow.settleTimeout = setTimeout(() => {
				try {
					if (flowsRef.current.get(flow.remixId) !== flow) {
						return;
					}
					flow.settleTimeout = undefined;
					const pending = flow.pendingMechanic;
					if (!pending) {
						return;
					}
					const latestMachinePhase = toSidebarPhase(
						deriveMachineEditorRemixPhase(getRemixMachine(flow.remixId)?.state()),
					);
					const stablePair = compareObjectSidebarRemixPhases(
						pending.legacyPhase,
						latestMachinePhase,
					);
					if (stablePair === 'match') {
						return;
					}
					if (stablePair !== 'pending') {
						reportCritical(flow, latestMachinePhase, stablePair.divergenceReason);
						return;
					}
					if (flow.reportedMechanics.has(pending.signature)) {
						return;
					}
					dispatchObjectSidebarRemixParityMechanicEvent(
						createAnalyticsEvent,
						{
							legacyPhase: pending.legacyPhase,
							machinePhase: latestMachinePhase,
							comparisonPoint: pending.comparisonPoint,
							action: 'observed',
							outcome: 'timing-skew',
							reason: 'awaiting-convergence',
							durationBucket: durationBucketForMs(SETTLE_MS),
						},
						dimensions(flow.entryPath),
					);
					pending.emitted = true;
					flow.reportedMechanics.add(pending.signature);
				} catch {
					reportObserverFailure('readParitySettleTimer');
					// An async parity read failure must not escape the timer callback.
				}
			}, SETTLE_MS);
		},
		[createAnalyticsEvent, dimensions, reportCritical, reportObserverFailure],
	);

	const observeFlow = useCallback(
		(remixId: string, entryPath: ObjectSidebarRemixParityDimensions['entryPath'] = 'unknown') => {
			const previous = flowsRef.current.get(remixId);
			try {
				previous?.unsubscribe();
			} catch {
				reportObserverFailure('unsubscribeParityFlow');
				// Replacement must continue even if an old subscription cannot detach cleanly.
			}
			if (previous?.settleTimeout) {
				clearTimeout(previous.settleTimeout);
			}
			const flow: Flow = {
				remixId,
				entryPath,
				legacyPhase: 'inactive',
				hasLegacyObservation: false,
				reportedDivergences: new Set(),
				reportedMechanics: new Set(),
				unsubscribe: () => {},
			};
			flow.unsubscribe = subscribeToMachine(remixId, () => {
				try {
					if (flowsRef.current.get(remixId) !== flow) {
						return;
					}
					evaluate(flow);
				} catch {
					reportObserverFailure('readParityMachineSubscriber');
					// A parity observer must never affect a machine transition.
				}
			});
			flowsRef.current.set(remixId, flow);
			activeFlowRef.current = remixId;
			evaluate(flow);
		},
		[evaluate, reportObserverFailure],
	);

	const recordLegacyPhase = useCallback(
		(
			token: symbol | undefined,
			legacyPhase: EditorRemixLifecyclePhase | ObjectSidebarRemixPhase,
			entryPath?: ObjectSidebarRemixParityDimensions['entryPath'],
		) => {
			const flow = token
				? [...flowsRef.current.values()].find((candidate) => candidate.observationToken === token)
				: undefined;
			if (!flow) {
				return;
			}
			flow.legacyPhase = toSidebarPhase(legacyPhase);
			flow.hasLegacyObservation = true;
			if (entryPath) {
				flow.entryPath = entryPath;
			}
			evaluate(flow);
		},
		[evaluate],
	);

	const bindObservationToken = useCallback((remixId: string, token: symbol) => {
		const flow = flowsRef.current.get(remixId);
		if (flow) {
			flow.observationToken = token;
		}
	}, []);

	const releaseFlow = useCallback(
		(remixId: string, capturedMachinePhase?: ObjectSidebarRemixPhase) => {
			const flow = flowsRef.current.get(remixId);
			if (!flow) {
				return;
			}
			if (flow.settleTimeout) {
				clearTimeout(flow.settleTimeout);
				flow.settleTimeout = undefined;
			}
			try {
				if (flow.hasLegacyObservation) {
					const machinePhase =
						capturedMachinePhase ??
						toSidebarPhase(deriveMachineEditorRemixPhase(getRemixMachine(remixId)?.state()));
					if (isTerminalSidebarPhase(flow.legacyPhase) && flow.legacyPhase !== machinePhase) {
						reportCritical(
							flow,
							machinePhase,
							isTerminalSidebarPhase(machinePhase)
								? 'terminal-outcome-mismatch'
								: 'released-unresolved',
						);
					} else if (!isTerminalSidebarPhase(flow.legacyPhase)) {
						reportCritical(flow, machinePhase, 'released-unresolved');
					}
				}
			} finally {
				try {
					flow.unsubscribe();
				} catch {
					reportObserverFailure('unsubscribeParityFlow');
					// Teardown must continue even if unsubscribe fails.
				}
				flowsRef.current.delete(remixId);
				if (activeFlowRef.current === remixId) {
					activeFlowRef.current = undefined;
				}
			}
		},
		[reportCritical, reportObserverFailure],
	);

	useEffect(
		() => () => {
			for (const flow of flowsRef.current.values()) {
				try {
					flow.unsubscribe();
				} catch {
					reportObserverFailureRef.current('unsubscribeParityFlow');
					// Unmount cleanup continues for remaining observations.
				}
				if (flow.settleTimeout) {
					clearTimeout(flow.settleTimeout);
				}
			}
			flowsRef.current.clear();
			activeFlowRef.current = undefined;
		},
		[],
	);

	return { observeFlow, bindObservationToken, recordLegacyPhase, releaseFlow };
};
